import {
  AccountTreeOutlined,
  AltRouteOutlined,
  ArrowForwardRounded,
  BadgeOutlined,
  CheckRounded,
  DescriptionOutlined,
  EventAvailableOutlined,
  FactCheckRounded,
  InfoRounded,
  PaletteOutlined,
  ScheduleRounded,
} from '@mui/icons-material';
import { Box, Button, ButtonBase, Divider, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';
import { createFocusRingStyles, designTokens, DialogSurface, InteractiveCard, ListRow, LoadingIndicator, PageBody, PillTabs, type PillTabItem } from '../../design-system';
import type { EmployeeHomeWorkflowSummary, HomePageKey, HomePageVisibility } from './model';
import {
  defaultHomeHeroThemeId,
  getHomeHero,
  homeHeroThemes,
  isHomeHeroThemeId,
  type HomeHeroThemeId,
} from './homeHeroThemes';
import { HomeServiceCard } from './HomeServiceCard';
import { homeServiceThemes } from './homeServiceThemes';

type LeaveBalance = {
  label: string;
  asOf: string;
  balance: string;
  accrued: number;
  used: string;
};

type EmployeeHomeDashboardProps = {
  displayName: string;
  viewerKey: string;
  visiblePages: HomePageVisibility;
  balance?: LeaveBalance;
  balanceLoading?: boolean;
  balanceError?: string;
  onRetryBalance?: () => void;
  workflowSummary: EmployeeHomeWorkflowSummary;
  onNavigate: (page: HomePageKey, query?: string) => void;
};

type Service = {
  label: string;
  page: HomePageKey;
  query?: string;
  icon: ReactNode;
  accent: string;
  tint: string;
};

type DashboardTab = 'today' | 'leave';

const dashboardTabs: readonly PillTabItem<DashboardTab>[] = [
  { value: 'today', label: '오늘', tabId: 'employee-dashboard-today-tab', panelId: 'employee-dashboard-today-panel' },
  { value: 'leave', label: '휴가', tabId: 'employee-dashboard-leave-tab', panelId: 'employee-dashboard-leave-panel' },
];

const services: readonly Service[] = [
  { label: '내 정보', page: 'my-profile', icon: <BadgeOutlined />, ...homeServiceThemes.profile },
  { label: '할 일', page: 'my-tasks', icon: <FactCheckRounded />, ...homeServiceThemes.tasks },
  { label: '조직도', page: 'directory', icon: <AccountTreeOutlined />, ...homeServiceThemes.directory },
  { label: '휴가', page: 'my-absence', icon: <EventAvailableOutlined />, ...homeServiceThemes.absence },
  { label: '증명서 발급', page: 'my-documents', icon: <DescriptionOutlined />, ...homeServiceThemes.documents },
  { label: '워크플로우', page: 'my-workflows', icon: <AltRouteOutlined />, ...homeServiceThemes.workflow },
];

const quickActions: readonly Service[] = [
  { label: '워크플로우', page: 'my-workflows', icon: <AltRouteOutlined />, ...homeServiceThemes.workflow },
  { label: '휴가 신청', page: 'my-absence', query: 'action=request', icon: <EventAvailableOutlined />, ...homeServiceThemes.absence },
  { label: '증명서 발급', page: 'my-documents', icon: <DescriptionOutlined />, ...homeServiceThemes.documents },
  { label: '동료 찾기', page: 'directory', icon: <AccountTreeOutlined />, ...homeServiceThemes.directory },
];

export function EmployeeHomeDashboard({
  displayName,
  viewerKey,
  visiblePages,
  balance,
  balanceLoading = false,
  balanceError,
  onRetryBalance,
  workflowSummary,
  onNavigate,
}: EmployeeHomeDashboardProps) {
  const heroStorageKey = `heyday:home-hero-theme:${viewerKey}`;
  const [requestedDashboardTab, setDashboardTab] = useState<DashboardTab>('today');
  const canAccessAbsence = visiblePages.has('my-absence');
  const dashboardTab = requestedDashboardTab === 'leave' && !canAccessAbsence ? 'today' : requestedDashboardTab;
  const visibleDashboardTabs = dashboardTabs.filter((item) => item.value !== 'leave' || canAccessAbsence);
  const [now, setNow] = useState(() => new Date());
  const [heroThemeId, setHeroThemeId] = useState<HomeHeroThemeId>(() => readStoredHeroTheme(heroStorageKey));
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const hero = getHomeHero(heroThemeId, now, viewerKey);
  const todayLabel = new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' }).format(now);
  const leaveUsage = balance ? Math.min(100, Number(balance.used) / Math.max(1, balance.accrued) * 100) : 0;
  const workflowCountLabel = (value?: number) => workflowSummary.loading ? '…' : workflowSummary.error ? '-' : `${value ?? 0}건`;
  const todayNotice = workflowSummary.loading
    ? '업무 현황을 확인하고 있어요.'
    : workflowSummary.error
      ? '업무 현황을 불러오지 못했어요.'
      : workflowSummary.taskCount
        ? `처리할 일이 ${workflowSummary.taskCount}건 있어요.`
        : '지금 처리할 일이 없어요.';
  const canAccessWorkflowRequests = visiblePages.has('my-workflow-requests');
  const visibleServices = services.filter((service) => visiblePages.has(service.page));
  const visibleQuickActions = quickActions.filter((action) => visiblePages.has(action.page));
  const activeDashboardPanel = dashboardTabs.find((item) => item.value === dashboardTab)!;
  const inactiveDashboardPanel = visibleDashboardTabs.find((item) => item.value !== dashboardTab);

  useEffect(() => {
    const intervalID = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(intervalID);
  }, []);

  useEffect(() => {
    setHeroThemeId(readStoredHeroTheme(heroStorageKey));
  }, [heroStorageKey]);

  const selectHeroTheme = (themeId: HomeHeroThemeId) => {
    setHeroThemeId(themeId);
    try {
      window.localStorage.setItem(heroStorageKey, themeId);
    } catch {
      // 로컬 저장소를 사용할 수 없는 환경에서도 현재 세션의 선택은 유지한다.
    }
    setThemeDialogOpen(false);
  };

  const rows: Array<{ label: string; value: string; page: HomePageKey; query?: string }> = dashboardTab === 'today'
    ? [
      { label: '할 일', value: workflowCountLabel(workflowSummary.taskCount), page: 'my-tasks' as const },
      ...(canAccessWorkflowRequests ? [
        { label: '진행 중 요청', value: workflowCountLabel(workflowSummary.inProgressRequestCount), page: 'my-workflow-requests' as const, query: 'status=IN_PROGRESS' },
        { label: '완료 요청', value: workflowCountLabel(workflowSummary.completedRequestCount), page: 'my-workflow-requests' as const, query: 'status=COMPLETED' },
      ] : []),
    ]
    : [
      { label: '발생 휴가', value: balance ? `${balance.accrued}일` : '-', page: 'my-absence' as const },
      { label: '사용 휴가', value: balance ? `${balance.used}일` : '-', page: 'my-absence' as const },
      { label: '잔여 휴가', value: balance ? `${balance.balance}일` : '-', page: 'my-absence' as const },
    ];
  const visibleRows = rows.filter((row) => visiblePages.has(row.page));
  const noticeError = dashboardTab === 'today' ? workflowSummary.error : balanceError;
  const noticeLoading = dashboardTab === 'today' ? workflowSummary.loading : balanceLoading;
  const noticeTone = noticeError ? 'danger' : noticeLoading ? 'neutral' : dashboardTab === 'leave' ? 'brand' : workflowSummary.taskCount ? 'warning' : 'success';
  const noticeColors = homeNoticeColors[noticeTone];
  const noticePage = dashboardTab === 'today' ? 'my-tasks' : 'my-absence';
  const retryNotice = dashboardTab === 'today' ? workflowSummary.onRetry : onRetryBalance;
  const noticeLabel = dashboardTab === 'today' ? todayNotice : balanceLoading ? '휴가 현황을 확인하고 있어요.' : balanceError ? '휴가 현황을 불러오지 못했어요.' : balance ? `사용할 수 있는 휴가는 ${balance.balance}일이에요.` : '휴가 현황과 신청 내역을 확인해 보세요.';

  return <Box data-testid="employee-home">
    <Box
      data-testid="employee-home-banner-shell"
      sx={{
        position: 'relative',
        '&:hover .home-hero-theme-button, &:focus-within .home-hero-theme-button': {
          opacity: 1,
          transform: 'translateY(0)',
          pointerEvents: 'auto',
        },
      }}
    >
      <Box
        aria-hidden
        data-testid="employee-home-banner"
        data-home-period={hero.period}
        data-home-theme={hero.theme.id}
        sx={{
          width: '100%',
          height: { xs: 140, sm: 180, md: 250 },
          bgcolor: hero.fallback,
          backgroundImage: `url(${hero.image})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          borderBottom: `1px solid ${designTokens.color.divider}`,
        }}
      />
      <ButtonBase
        className="home-hero-theme-button"
        aria-label={`현재 ${hero.theme.label} 테마. 히어로 테마 변경`}
        data-testid="hero-theme-change-button"
        onClick={() => setThemeDialogOpen(true)}
        sx={{
          position: 'absolute',
          zIndex: 1,
          top: { xs: 10, sm: 14 },
          right: { xs: 12, sm: 20 },
          minHeight: { xs: 44, sm: 36 },
          '@media (pointer: coarse)': { minHeight: 44 },
          px: 1.4,
          borderRadius: designTokens.radius.button,
          gap: 0.7,
          bgcolor: designTokens.overlay.scrimSurface,
          color: designTokens.color.inverseText,
          border: `1px solid ${designTokens.overlay.scrimBorder}`,
          boxShadow: designTokens.shadow.floatingControl,
          backdropFilter: 'blur(10px)',
          opacity: { xs: 1, md: 0 },
          transform: { xs: 'none', md: 'translateY(-4px)' },
          pointerEvents: { xs: 'auto', md: 'none' },
          transition: 'opacity 140ms ease, transform 140ms ease, background-color 140ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '&:hover': { bgcolor: designTokens.overlay.scrimHoverSurface },
          '&:focus-visible': { ...focusSx, opacity: 1, transform: 'translateY(0)', pointerEvents: 'auto' },
        }}
      >
        <PaletteOutlined sx={{ fontSize: 18 }} />
        <Typography component="span" sx={{ ...designTokens.typography.ui, fontWeight: 700 }}>테마 변경</Typography>
      </ButtonBase>
    </Box>
    <HeroThemeDialog
      open={themeDialogOpen}
      selectedThemeId={heroThemeId}
      onClose={() => setThemeDialogOpen(false)}
      onSelect={selectHeroTheme}
    />
    <PageBody width="standard">
    <Box
      data-testid="employee-home-content"
      sx={{
        pt: {
          xs: `${designTokens.layout.overviewBodyStartGapCompact}px`,
          md: `${designTokens.layout.overviewBodyStartGap}px`,
        },
      }}
    >
      <Box
        component="section"
        aria-labelledby="employee-welcome-heading"
        sx={{
          minHeight: { xs: 52, md: 58 },
          display: 'grid',
          gridTemplateColumns: homeContentColumns,
          columnGap: { lg: 3 },
          rowGap: 1,
          alignItems: 'center',
        }}
      >
        <Typography
          id="employee-welcome-heading"
          component="h1"
          sx={{
            m: 0,
            minWidth: 0,
            fontSize: { xs: 23, sm: 28, md: 30 },
            lineHeight: 1.22,
            fontWeight: 800,
            color: designTokens.color.primaryText,
            letterSpacing: '-0.045em',
            wordBreak: 'keep-all',
          }}
        >
          {`${hero.greeting}, ${displayName}님`}
        </Typography>
        <Typography
          data-testid="employee-home-today"
          component="time"
          dateTime={new Intl.DateTimeFormat('sv-SE').format(now)}
          sx={{
            fontSize: { xs: 12, sm: 13.5 },
            lineHeight: 1.4,
            fontWeight: 600,
            color: designTokens.color.secondaryText,
            whiteSpace: 'nowrap',
            textAlign: 'left',
          }}
        >
          {`오늘은 ${todayLabel} 입니다`}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: homeContentColumns, gap: { xs: 3.5, lg: 3 }, mt: { xs: 3.5, md: 4.5 } }}>
        <Box component="section" aria-labelledby="employee-services-heading">
          <Typography id="employee-services-heading" component="h2" sx={sectionTitleSx}>자주 찾는 서비스</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mt: 1.75 }}>
            {visibleServices.map((service) => <HomeServiceCard key={service.page} {...service} onClick={() => onNavigate(service.page)} />)}
          </Box>
        </Box>

        <Box component="section" aria-labelledby="employee-quick-heading">
          <Typography id="employee-quick-heading" component="h2" sx={sectionTitleSx}>빠른 실행</Typography>
          <Stack divider={<Divider />} sx={{ mt: 1.75, p: 1, border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.canvas }}>
            {visibleQuickActions.map((action) => <QuickAction key={action.page} icon={action.icon} label={action.label} color={action.accent} tint={action.tint} onClick={() => onNavigate(action.page, action.query)} />)}
          </Stack>
        </Box>
      </Box>

      <Box component="section" aria-labelledby="employee-dashboard-heading" sx={{ mt: 5.5 }}>
        <Typography id="employee-dashboard-heading" component="h2" sx={sectionTitleSx}>대시보드</Typography>
        <Box sx={{ mt: 1.5 }}>
          <PillTabs value={dashboardTab} items={visibleDashboardTabs} ariaLabel="대시보드 보기" onChange={setDashboardTab} />
        </Box>
        <Box id={activeDashboardPanel.panelId} role="tabpanel" aria-labelledby={activeDashboardPanel.tabId}>
        <ButtonBase
          data-testid="employee-home-notice"
          data-tone={noticeTone}
          aria-label={noticeLoading ? noticeLabel : undefined}
          aria-busy={noticeLoading}
          disabled={noticeLoading || (noticeError ? !retryNotice : !visiblePages.has(noticePage))}
          onClick={() => noticeError ? retryNotice?.() : onNavigate(noticePage)}
          sx={{ width: '100%', minHeight: 52, mt: 1.5, px: 1.5, py: 1, gap: 0.75, borderRadius: designTokens.radius.surface, justifyContent: 'flex-start', bgcolor: noticeColors.surface, color: noticeColors.text, '&:hover': { bgcolor: noticeColors.surface }, '&:focus-visible': focusSx }}
        >
          {noticeLoading ? <LoadingIndicator label={noticeLabel} /> : <>
            {noticeTone === 'success' ? <CheckRounded sx={{ fontSize: 18 }} /> : <InfoRounded sx={{ fontSize: 18 }} />}
            <Typography sx={{ flex: 1, textAlign: 'left', ...designTokens.typography.ui, fontWeight: 650, letterSpacing: '-0.02em' }}>{noticeLabel}</Typography>
          </>}
          {!noticeLoading && (noticeError ? Boolean(retryNotice) : visiblePages.has(noticePage)) && <Typography component="span" sx={{ flexShrink: 0, ...designTokens.typography.supporting, fontWeight: 700 }}>{noticeError ? '다시 시도' : '확인'}</Typography>}
        </ButtonBase>
        <Box sx={{ mt: 1.5, minHeight: 230, border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.canvas, p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                <Typography sx={{ ...designTokens.typography.ui, fontWeight: 650, color: designTokens.color.secondaryText }}>{dashboardTab === 'today' ? '오늘 필요한 일' : (balance?.label || '내 휴가')}</Typography>
                <ScheduleRounded sx={{ fontSize: 14, color: designTokens.color.secondaryText }} />
                <Typography sx={{ ...designTokens.typography.supporting, color: designTokens.color.secondaryText }}>{dashboardTab === 'today' ? '현재 기준' : `${balance?.asOf || '현재'} 기준`}</Typography>
              </Stack>
              {dashboardTab === 'today' && (workflowSummary.loading
                ? <Skeleton aria-label="업무 현황을 불러오는 중입니다." variant="rounded" width={96} height={32} sx={{ mt: 0.5 }} />
                : workflowSummary.error
                  ? <Typography sx={{ mt: 0.45, ...designTokens.typography.metricValue }}>—</Typography>
                  : <Typography sx={{ mt: 0.45, ...designTokens.typography.metricValue, fontWeight: 780, letterSpacing: '-0.045em' }}>{workflowSummary.taskCount ?? 0}건</Typography>)}
              {dashboardTab === 'leave' && <LeaveSummary balance={balance} loading={balanceLoading} error={balanceError} onRetry={onRetryBalance} usage={leaveUsage} />}
            </Box>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.max(1, visibleRows.length)}, minmax(0, 1fr))` }, mt: 2.25, borderTop: `1px solid ${designTokens.color.subtleSurface}` }}>
            {visibleRows.map((row) => <ButtonBase key={row.label} onClick={() => onNavigate(row.page, row.query)} sx={{ minHeight: 96, px: { xs: 0, sm: 1.5 }, py: 1.5, justifyContent: 'space-between', borderBottom: { xs: `1px solid ${designTokens.color.subtleSurface}`, sm: 0 }, borderRight: { xs: 0, sm: `1px solid ${designTokens.color.subtleSurface}` }, '&:last-child': { borderRight: 0 }, '&:hover': { bgcolor: designTokens.color.sidebarSurface }, '&:focus-visible': focusSx }}>
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ ...designTokens.typography.supporting, color: designTokens.color.mutedText }}>{row.label}</Typography>
                <Typography sx={{ mt: 0.45, ...designTokens.typography.componentTitle, fontWeight: 730, color: designTokens.color.primaryText, letterSpacing: '-0.035em' }}>{row.value}</Typography>
              </Box>
              <ArrowForwardRounded sx={{ fontSize: 18, color: designTokens.color.faintText }} />
            </ButtonBase>)}
          </Box>
        </Box>
        </Box>
        {inactiveDashboardPanel && <Box id={inactiveDashboardPanel.panelId} role="tabpanel" aria-labelledby={inactiveDashboardPanel.tabId} hidden />}
      </Box>

    </Box>
    </PageBody>
  </Box>;
}

function readStoredHeroTheme(storageKey: string): HomeHeroThemeId {
  if (typeof window === 'undefined') return defaultHomeHeroThemeId;
  try {
    const savedThemeId = window.localStorage.getItem(storageKey);
    return isHomeHeroThemeId(savedThemeId) ? savedThemeId : defaultHomeHeroThemeId;
  } catch {
    return defaultHomeHeroThemeId;
  }
}

function HeroThemeDialog({
  open,
  selectedThemeId,
  onClose,
  onSelect,
}: {
  open: boolean;
  selectedThemeId: HomeHeroThemeId;
  onClose: () => void;
  onSelect: (themeId: HomeHeroThemeId) => void;
}) {
  return <DialogSurface
    open={open}
    onClose={onClose}
    title="히어로 테마"
    help="선택한 테마의 장면이 시간에 맞춰 자동으로 바뀝니다."
    closeLabel="테마 선택 닫기"
    size="standard"
  >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        {homeHeroThemes.map((theme) => {
          const selected = theme.id === selectedThemeId;
          return <InteractiveCard
            key={theme.id}
            aria-pressed={selected}
            selected={selected}
            data-testid={`hero-theme-option-${theme.id}`}
            onClick={() => onSelect(theme.id)}
            sx={{
              p: 0.8,
              display: 'block',
            }}
          >
            <Box sx={{ position: 'relative', height: 74, overflow: 'hidden', borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.subtleSurface }}>
              <Box component="img" src={theme.preview} alt="" loading="lazy" sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
              {selected && <Box aria-hidden sx={{ position: 'absolute', top: 8, right: 8, width: 26, height: 26, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.circle, bgcolor: designTokens.color.action, color: designTokens.color.inverseText, boxShadow: designTokens.shadow.selectedControl }}><CheckRounded sx={{ fontSize: 17 }} /></Box>}
            </Box>
            <Box sx={{ px: 0.7, pt: 1.1, pb: 0.45 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 780, color: designTokens.color.primaryText }}>{theme.label}</Typography>
                <Typography sx={{ ...designTokens.typography.supporting, color: designTokens.color.mutedText, whiteSpace: 'nowrap' }}>{theme.selectionLabel}</Typography>
              </Stack>
              <Typography sx={{ mt: 0.3, ...designTokens.typography.supporting, color: designTokens.color.secondaryText }}>{theme.description}</Typography>
            </Box>
          </InteractiveCard>;
        })}
      </Box>
  </DialogSurface>;
}

function QuickAction({ icon, label, color, tint, onClick }: { icon: ReactNode; label: string; color: string; tint: string; onClick: () => void }) {
  return <ListRow label={label} onClick={onClick}
    icon={<Box aria-hidden sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: designTokens.radius.surface, display: 'grid', placeItems: 'center', bgcolor: tint, color, '& svg': { fontSize: 23 } }}>{icon}</Box>}
  />;
}

function LeaveSummary({ balance, loading, error, onRetry, usage }: { balance?: LeaveBalance; loading: boolean; error?: string; onRetry?: () => void; usage: number }) {
  if (loading) return <Skeleton aria-label="휴가 정보를 불러오는 중입니다." variant="rounded" width={160} height={40} sx={{ mt: 0.5 }} />;
  if (error) return <Stack direction="row" spacing={1} sx={{ minHeight: 48, alignItems: 'center' }}><Typography sx={{ ...designTokens.typography.ui, color: designTokens.color.dangerText }}>휴가 정보를 불러오지 못했어요.</Typography>{onRetry && <Button variant="weak" onClick={onRetry}>다시 시도</Button>}</Stack>;
  return <Box sx={{ maxWidth: 440 }}>
    <Stack direction="row" sx={{ mt: 0.3, alignItems: 'baseline' }}><Typography sx={{ ...designTokens.typography.metricValue, fontWeight: 780, letterSpacing: '-0.045em' }}>{balance?.balance || '-'}</Typography><Typography sx={{ ml: 0.45, ...designTokens.typography.ui, fontWeight: 650 }}>일</Typography></Stack>
    <LinearProgress variant="determinate" value={usage} aria-label="휴가 사용률" sx={{ mt: 1.1 }} />
  </Box>;
}

const sectionTitleSx = {
  m: 0,
  ...designTokens.typography.sectionTitle,
  fontWeight: 700,
  color: designTokens.color.primaryText,
  letterSpacing: '-0.04em',
} as const;

const homeContentColumns = { xs: '1fr', lg: 'minmax(0, 2fr) minmax(280px, 1fr)' } as const;

const focusSx = createFocusRingStyles({ inset: true });

const homeNoticeColors = {
  neutral: { surface: designTokens.color.subtleSurface, text: designTokens.color.secondaryText },
  brand: { surface: designTokens.color.actionSoft, text: designTokens.color.actionDark },
  success: { surface: designTokens.color.successSoft, text: designTokens.color.successText },
  warning: { surface: designTokens.color.warningSoft, text: designTokens.color.warningDark },
  danger: { surface: designTokens.color.dangerSoft, text: designTokens.color.dangerText },
} as const;
